import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiX, FiChevronDown, FiMenu, FiMail } from 'react-icons/fi';
import { useData } from '../../context/DataContext';
import SearchDropdown from '../SearchDropdown';

const Navbar = () => {
    const { categories, subcategories, loading } = useData();
    const location = useLocation();
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Get subcategories for the active dropdown
    const getSubcategoriesForCategory = (categoryId) => {
        if (!subcategories) return [];
        return subcategories.filter(subcat => subcat.category === categoryId ||
            (subcat.category && subcat.category._id === categoryId));
    };

    // Function to organize subcategories into columns
    const organizeIntoColumns = (subcats, columnCount = 3) => {
        if (!subcats || subcats.length === 0) return [];

        const result = Array(columnCount).fill().map(() => []);
        const itemsPerColumn = Math.ceil(subcats.length / columnCount);

        subcats.forEach((item, index) => {
            const columnIndex = Math.min(Math.floor(index / itemsPerColumn), columnCount - 1);
            result[columnIndex].push(item);
        });

        // Filter out empty columns
        return result.filter(column => column.length > 0);
    };

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrolled]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.category-dropdown')) {
                setActiveDropdown(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    // Close dropdown when navigating
    useEffect(() => {
        setActiveDropdown(null);
        setMobileMenuOpen(false);
    }, [location.pathname]);

    // Prevent body scroll when mobile menu is open (only control vertical scroll)
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflowY = 'hidden';
        } else {
            document.body.style.overflowY = '';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.style.overflowY = '';
        };
    }, [mobileMenuOpen]);

    const handleSearchToggle = () => {
        setSearchOpen(!searchOpen);
        if (searchOpen) {
            setSearchQuery('');
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        // Handle search submission
        console.log('Search for:', searchQuery);
        setSearchOpen(false);
        setSearchQuery('');
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
    };

    const handleSearchSelect = (item) => {
        // This will be handled by the SearchDropdown component's navigation
        setSearchOpen(false);
        setSearchQuery('');
    };

    const handleCategoryHover = (categoryId) => {
        setActiveDropdown(categoryId);
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    return (
        <header 
            className="sticky top-0 z-50" 
            style={{ 
                backgroundColor: '#f8f8f8',
                color: '#111827'
            }}
        >
            <div className="w-full">
                {/* Top row with search, logo, and auth buttons */}
                <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
                    {/* Mobile menu button - only visible on mobile */}
                    <div className="lg:hidden">
                        <button
                            onClick={handleMobileMenuToggle}
                            className="p-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#ab3d22] focus:ring-offset-2 rounded-md"
                            aria-label="Toggle mobile menu"
                        >
                            <FiMenu className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Search bar - hidden on mobile, visible on desktop */}
                    <div className="hidden lg:flex">
                        <SearchDropdown
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onSelect={handleSearchSelect}
                            placeholder="Search by concern, ingredient, or brand…"
                            className="w-80"
                        />
                    </div>

                    {/* Centered radiance logo */}
                    <div className="flex items-center gap-2 absolute left-1/2 transform -translate-x-1/2">
                        <button 
                            onClick={() => {
                                navigate('/');
                                // Always scroll to top when clicking logo
                                setTimeout(() => {
                                    window.scrollTo({
                                        top: 0,
                                        behavior: 'smooth'
                                    });
                                }, 100);
                            }}
                            className="text-2xl sm:text-3xl font-bold lowercase hover:text-[#8a2f1a] transition-colors"
                            style={{ 
                                fontFamily: 'Poppins, sans-serif', 
                                fontWeight: 700,
                                color: '#ab3d22'
                            }}
                        >
                            radiance.
                        </button>
                    </div>

                    {/* Subscribe button/icon on the right */}
                    <div className="flex items-center gap-3">
                        {/* Mobile: Icon only */}
                        <a
                            href="http://eepurl.com/jpTPXQ"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="lg:hidden p-2 text-[#ab3d22] hover:bg-red-50 rounded-full transition-colors"
                            aria-label="Subscribe"
                        >
                            <FiMail className="h-6 w-6" />
                        </a>
                        
                        {/* Desktop: Full button */}
                        <a
                            href="http://eepurl.com/jpTPXQ"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hidden lg:flex bg-[#ab3d22] text-white px-4 py-1.5 rounded-full font-medium hover:bg-[#8a2f1a] transition-colors text-sm"
                        >
                            SUBSCRIBE
                        </a>
                    </div>
                </div>

                {/* Mobile Menu - only visible on mobile */}
                {mobileMenuOpen && (
                    <>
                        {/* Backdrop overlay - click to close */}
                        <div 
                            className="lg:hidden fixed inset-0 bg-black bg-opacity-20 z-30"
                            onClick={handleMobileMenuToggle}
                        />
                        
                        {/* Menu Content */}
                        <div className="lg:hidden absolute left-0 right-0 z-40"
                            style={{ 
                                backgroundColor: '#f8f8f8',
                                top: '100%',
                                maxHeight: 'calc(100vh - 4rem)',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                            <div 
                                className="overflow-y-auto py-4 px-4 sm:px-6" 
                                style={{ 
                                    maxHeight: 'calc(100vh - 4rem)',
                                    WebkitOverflowScrolling: 'touch',
                                    overscrollBehavior: 'contain',
                                    touchAction: 'pan-y'
                                }}
                            >
                        <nav className="space-y-2">
                            {/* All Products Link for Mobile */}
                            <Link
                                to="/products"
                                className={`block px-3 py-2 text-lg font-bold rounded-md transition-colors ${
                                    location.pathname === '/products'
                                        ? 'text-[#ab3d22] bg-red-50'
                                        : 'text-gray-700 hover:text-[#ab3d22] hover:bg-gray-50'
                                }`}
                            >
                                All Products
                            </Link>
                            
                            {loading ? (
                                <div className="space-y-2">
                                    <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-8 w-20 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-8 w-28 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : categories && categories.length > 0 ? categories.map((category) => {
                                const categorySubcats = getSubcategoriesForCategory(category._id);
                                
                                return (
                                    <div key={category._id} className="space-y-1">
                                        <Link
                                            to={`/category/${category.slug}`}
                                            className={`block px-3 py-2 text-lg font-bold rounded-md transition-colors ${location.pathname === `/category/${category.slug}` ||
                                                    location.pathname.startsWith(`/category/${category.slug}/`)
                                                    ? 'text-[#ab3d22] bg-red-50'
                                                    : 'text-gray-700 hover:text-[#ab3d22] hover:bg-gray-50'
                                                }`}
                                        >
                                            {category.name}
                                        </Link>
                                        
                                        {/* Mobile subcategories */}
                                        {categorySubcats.length > 0 && (
                                            <div className="ml-4 space-y-1">
                                                {categorySubcats.slice(0, 4).map((subcat) => (
                                                    <Link
                                                        key={subcat._id}
                                                        to={`/category/${category.slug}/${subcat.slug}`}
                                                        className="block px-3 py-1 text-sm text-gray-600 hover:text-[#ab3d22] hover:bg-gray-50 rounded-md transition-colors"
                                                    >
                                                        {subcat.name}
                                                    </Link>
                                                ))}
                                                {categorySubcats.length > 4 && (
                                                    <Link
                                                        to={`/category/${category.slug}`}
                                                        className="block px-3 py-1 text-sm text-[#ab3d22] font-medium hover:bg-gray-50 rounded-md transition-colors"
                                                    >
                                                        View all {category.name} →
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }) : (
                                <div className="space-y-2 text-gray-500">
                                    <Link to="/category/skincare" className="block px-3 py-2 text-lg font-bold hover:text-[#ab3d22] transition-colors uppercase">Skincare</Link>
                                    <Link to="/category/hair" className="block px-3 py-2 text-lg font-bold hover:text-[#ab3d22] transition-colors uppercase">Hair</Link>
                                    <Link to="/category/makeup" className="block px-3 py-2 text-lg font-bold hover:text-[#ab3d22] transition-colors uppercase">Makeup</Link>
                                    <Link to="/category/body" className="block px-3 py-2 text-lg font-bold hover:text-[#ab3d22] transition-colors uppercase">Body</Link>
                                    <Link to="/category/wellness" className="block px-3 py-2 text-lg font-bold hover:text-[#ab3d22] transition-colors uppercase">Wellness</Link>
                                </div>
                            )}
                        </nav>
                            </div>
                        </div>
                    </>
                )}

                {/* Mobile Search - only visible on mobile - hidden when menu is open */}
                {!mobileMenuOpen && (
                <div className="lg:hidden py-3 px-4 sm:px-6">
                    <SearchDropdown
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onSelect={handleSearchSelect}
                        placeholder="Search by concern, ingredient, or brand…"
                        className="w-full"
                    />
                </div>
                )}

                {/* Desktop Navigation categories row - hidden on mobile */}
                <div className="hidden lg:block">
                    <div className="py-3">
                        <nav 
                            className="flex items-center justify-center gap-8 text-lg font-bold" 
                            style={{ color: '#374151' }}
                        >
                            {/* All Products Link */}
                            <Link
                                to="/products"
                                className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase"
                                style={{ 
                                    color: location.pathname === '/products'
                                        ? '#ab3d22'
                                        : '#374151'
                                }}
                            >
                                All Products
                            </Link>
                            
                            {/* Categories with Dropdowns */}
                            {loading ? (
                                <div className="flex items-center gap-8">
                                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            ) : categories && categories.length > 0 ? categories.map((category) => {
                                const categorySubcats = getSubcategoriesForCategory(category._id);
                                const subcatColumns = organizeIntoColumns(categorySubcats,
                                    categorySubcats.length > 9 ? 3 : categorySubcats.length > 4 ? 2 : 1);
                                const dropdownWidth = subcatColumns.length === 1 ? 'w-48' :
                                    subcatColumns.length === 2 ? 'w-80' : 'w-96';

                                return (
                                    <div
                                        key={category._id}
                                        className="relative category-dropdown"
                                        onMouseEnter={() => handleCategoryHover(category._id)}
                                        onMouseLeave={() => setActiveDropdown(null)}
                                    >
                                        <Link
                                            to={`/category/${category.slug}`}
                                            className="font-bold text-lg inline-flex items-center hover:text-[#ab3d22] transition-colors uppercase"
                                            style={{ 
                                                color: location.pathname === `/category/${category.slug}` ||
                                                    location.pathname.startsWith(`/category/${category.slug}/`)
                                                    ? '#ab3d22'
                                                    : '#374151'
                                            }}
                                        >
                                            {category.name}
                                            <FiChevronDown className="ml-1 h-4 w-4" />
                                        </Link>

                                        {/* Dropdown Menu */}
                                        <div
                                            className={`absolute left-0 top-full pt-2 ${dropdownWidth} transition-all duration-300 transform origin-top ${activeDropdown === category._id
                                                    ? 'opacity-100 scale-100'
                                                    : 'opacity-0 scale-95 pointer-events-none'
                                                }`}
                                        >
                                            <div className="bg-[#f8f8f8] shadow-lg rounded-md py-3 border border-gray-100">
                                                <div className="px-4 pb-2 mb-2 border-b border-gray-100">
                                                    <Link
                                                        to={`/category/${category.slug}`}
                                                        className="text-sm font-medium text-[#ab3d22] hover:underline"
                                                    >
                                                        View All {category.name}
                                                    </Link>
                                                </div>

                                                <div className="flex">
                                                    {subcatColumns.map((column, colIndex) => (
                                                        <div key={colIndex} className={`flex-1 ${colIndex > 0 ? 'border-l border-gray-100' : ''}`}>
                                                            {column.map((subcat) => (
                                                                <Link
                                                                    key={subcat._id}
                                                                    to={`/category/${category.slug}/${subcat.slug}`}
                                                                    className="block px-4 py-1.5 text-sm text-gray-700 hover:text-[#ab3d22] hover:bg-gray-50 group"
                                                                >
                                                                    <span className="relative inline-block">
                                                                        {subcat.name}
                                                                        <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#ab3d22] transition-all duration-300 group-hover:w-full"></span>
                                                                    </span>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="flex items-center gap-8 text-gray-500">
                                    <Link to="/category/skincare" className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase">Skincare</Link>
                                    <Link to="/category/hair" className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase">Hair</Link>
                                    <Link to="/category/makeup" className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase">Makeup</Link>
                                    <Link to="/category/body" className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase">Body</Link>
                                    <Link to="/category/wellness" className="font-bold text-lg hover:text-[#ab3d22] transition-colors uppercase">Wellness</Link>
                                </div>
                            )}
                        </nav>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;